/**
 * Murdoku domain model.
 *
 * A puzzle is an N×N grid partitioned into contiguous named rooms. N characters stand on it —
 * N-1 suspects plus the victim — with exactly one character per row and per column. The murderer
 * is the single character sharing the victim's room. Players deduce every position from clues.
 *
 * Clues are structured data, never text: each is a list of atoms that a renderer turns into a
 * sentence in the player's language. That is what makes the game translatable.
 */

/** A cell index into a row-major N×N grid: `row * size + col`. */
export type CellIdx = number;

/** Difficulty tiers, easiest first. The index into this array is the numeric tier (0-4). */
export const TIERS = ["very_easy", "easy", "medium", "hard", "expert"] as const;
export type Tier = (typeof TIERS)[number];

export type Gender = "m" | "f";

/** Compass directions. Cardinals are used for offsets; the diagonals only for `compass` atoms. */
export type Cardinal = "N" | "S" | "E" | "W";
export type Diagonal = "NE" | "NW" | "SE" | "SW";
export type Direction = Cardinal | Diagonal;

/**
 * Furniture kinds. `occupiable` kinds can be stood on; everything else blocks a cell.
 * Kinds are keys, not display names — the words come from the language catalogues.
 */
export const OCCUPIABLE_KINDS = ["bed", "chair", "carpet", "bench"] as const;
export const BLOCKING_KINDS = [
  "table",
  "cabinet",
  "plant",
  "tree",
  "statue",
  "crate",
  "pond",
  "stove",
] as const;
export type OccupiableKind = (typeof OCCUPIABLE_KINDS)[number];
export type BlockingKind = (typeof BLOCKING_KINDS)[number];
export type ObjectKind = OccupiableKind | BlockingKind;

export function isOccupiableKind(kind: ObjectKind): kind is OccupiableKind {
  return (OCCUPIABLE_KINDS as readonly string[]).includes(kind);
}

/** One character on the grid. The victim is always the last index and is displayed as "V". */
export interface Character {
  index: number;
  /** Display letter: "A", "B", … for suspects, "V" for the victim. */
  letter: string;
  name: string;
  gender: Gender;
  isVictim: boolean;
}

export interface Room {
  id: number;
  /** Cells belonging to this room, ascending. */
  cells: CellIdx[];
  /** Where a renderer should anchor the room's name label. */
  labelAnchor: CellIdx;
}

/**
 * A piece of furniture. Multi-cell objects (a long table, a double bed) are ONE object so that
 * "beside the table" counts the object once however many cells it covers.
 */
export interface BoardObject {
  id: string;
  kind: ObjectKind;
  cells: CellIdx[];
  occupiable: boolean;
}

/** The board: geometry only, no characters and no clues. */
export interface Board {
  size: number;
  /** Room id per cell, row-major, length size². */
  regions: number[];
  rooms: Room[];
  objects: BoardObject[];
  /** Object index per cell, or -1 for bare floor. Derived from `objects`. */
  objectAt: number[];
  /** Whether a character may stand on each cell. Derived from `objects`. */
  occupiable: boolean[];
}

/**
 * A clue atom — one indivisible statement about a character. A clue is a list of atoms, which the
 * renderer may merge into a single sentence ("alone" + "room" -> "was alone in the Kitchen").
 *
 * Semantics that matter:
 * - `beside` means orthogonally adjacent AND in the same room.
 * - `rowOffset`/`colOffset`/`compass` are NOT room-constrained: they speak about the whole grid.
 * - `alone` means nobody else is in that character's room — not even the victim.
 */
export type Atom =
  | { kind: "room"; char: number; roomId: number }
  | { kind: "notRoom"; char: number; roomId: number }
  | { kind: "orRoom"; char: number; roomIds: [number, number] }
  | { kind: "on"; char: number; objectKind: ObjectKind }
  | { kind: "onlyOn"; char: number; objectKind: ObjectKind }
  | { kind: "beside"; char: number; objectKind: ObjectKind }
  | { kind: "notBeside"; char: number; objectKind: ObjectKind }
  | { kind: "onlyBeside"; char: number; objectKind: ObjectKind }
  | { kind: "otherBeside"; char: number; objectKind: ObjectKind }
  | { kind: "alone"; char: number }
  | { kind: "with"; char: number; other: number }
  | { kind: "aloneWith"; char: number; other: number }
  | { kind: "absRow"; char: number; row: number }
  | { kind: "absCol"; char: number; col: number }
  | { kind: "rowOffset"; char: number; other: number; dir: "N" | "S"; distance: number }
  | { kind: "colOffset"; char: number; other: number; dir: "E" | "W"; distance: number }
  | { kind: "compass"; char: number; other: number; dir: Diagonal }
  | { kind: "inCorner"; char: number }
  | { kind: "notCorner"; char: number };

export type AtomKind = Atom["kind"];

/** Board-wide statements, shown apart from the character clues. At most 3 per puzzle. */
export type GeneralClue =
  | { kind: "noEmptyRoom" }
  | { kind: "oneOnType"; objectKind: ObjectKind };

/** All the atoms attached to one character's clue card. */
export interface Clue {
  char: number;
  atoms: Atom[];
}

/** Inference techniques the solver can apply, in escalating order of difficulty. */
export const TECHNIQUES = [
  "nakedSingle",
  "victimRule",
  "aloneRule",
  "withRule",
  "aloneWithRule",
  "offsetCrossCheck",
  "relational",
  "uniqueObject",
  "generalClue",
  "lineLock",
  "mustFillLine",
  "lastSquareInLine",
  "forwardCheck",
  "nakedPair",
  "xBlock",
] as const;
export type Technique = (typeof TECHNIQUES)[number];

/** The techniques we consider "advanced" — a puzzle needing one is at least Medium. */
export const ADVANCED_TECHNIQUES: readonly Technique[] = [
  "nakedPair",
  "xBlock",
  "lastSquareInLine",
  "forwardCheck",
];

/**
 * How demanding each technique is. Difficulty is defined by the deepest level a puzzle forces a
 * solver to reach, and generation steers difficulty by carving only as far as a given depth can
 * still follow.
 *   1 — read the clues and their immediate consequences
 *   2 — reason about whole rows and columns
 *   3 — look ahead, or spot a pattern across several characters
 */
export const TECHNIQUE_DEPTH: Record<Technique, 1 | 2 | 3> = {
  nakedSingle: 1,
  victimRule: 1,
  aloneRule: 1,
  withRule: 1,
  aloneWithRule: 1,
  uniqueObject: 1,
  generalClue: 1,
  offsetCrossCheck: 1,
  relational: 1,
  lineLock: 2,
  mustFillLine: 2,
  lastSquareInLine: 3,
  forwardCheck: 3,
  nakedPair: 3,
  xBlock: 3,
};

/** One recorded inference, used for difficulty grading, hints and the solution walkthrough. */
export interface DeductionStep {
  technique: Technique;
  /** Characters this step resolved, if any. */
  char?: number;
  /** Cell the character was placed on, when the step was a placement. */
  cell?: CellIdx;
  /** Cells eliminated by the step. */
  eliminated?: CellIdx[];
  /** Human-readable rationale key + params for the i18n layer. */
  detail: Record<string, unknown>;
}

export interface TechniqueProfile {
  /** Distinct techniques the ladder needed, in first-use order. */
  used: Technique[];
  steps: number;
  advancedSteps: number;
  /** Characters pinned by their own clues alone, before any cross-reasoning. */
  instantPins: number;
  /** Count of grid-coordinate atoms (absRow/absCol/offsets/compass) — the powerful, rationed kind. */
  positionalAtoms: number;
}

/** A complete, playable puzzle. */
export interface PuzzleDefinition {
  size: number;
  seed: string;
  templateId: string;
  board: Board;
  characters: Character[];
  clues: Clue[];
  generalClues: GeneralClue[];
  /** Cell per character index. */
  solution: CellIdx[];
  murdererIndex: number;
  tier: Tier;
  parTimeSec: number;
  techniqueProfile: TechniqueProfile;
}

/** The victim is always the last character. */
export function victimIndex(characters: Character[]): number {
  return characters.length - 1;
}
